import ShopSection from './components/ShopSection'
import { useState, useEffect } from 'react'
import './App.css'
import NewsSection from './NewsSection'
import SEO from './components/SEO'

function App() {
  const [activeTab, setActiveTab] = useState('home')
  const [customDomain, setCustomDomain] = useState<string | null>(null)
  const [currentUrl, setCurrentUrl] = useState<string>('')
  const [showDomainNotification, setShowDomainNotification] = useState(false)
  
  // 检测当前域名和自定义域名
  useEffect(() => {
    // 获取当前URL
    const url = window.location.href
    setCurrentUrl(url)
    
    // 检查是否是pages.dev域名
    if (url.includes('.pages.dev')) {
      // 如果是pages.dev，设置对应的自定义域名
      setCustomDomain('https://hellolasa.com')
      // 显示通知
      setShowDomainNotification(true)
    } else {
      // 如果不是pages.dev，当前域名可能就是自定义域名
      setCustomDomain(url)
      setShowDomainNotification(false)
    }
  }, [])

  // Navigation items
  const navItems: Array<{id: string; label: string}> = []

  // Other destinations for cross-linking
  const destinations = [
    { name: 'HelloXinjiang', url: 'https://helloxinjiang.pages.dev', emoji: '🏔️', description: 'Xinjiang Beauty' },
    { name: 'HelloYichang', url: 'https://helloyichang.pages.dev', emoji: '🌊', description: 'Three Gorges Gateway' },
    { name: 'HelloXizang', url: 'https://helloxizang.pages.dev', emoji: '🗻', description: 'Tibet Highlands' },
    { name: 'Wudang Time Chamber', url: 'https://wudangtimechamber.com', emoji: '🧘', description: 'Sacred Mountain Retreat for Men' }
  ]

  // Main attractions for Lasa
  const attractions = [
    {
      id: 1,
      title: 'Potala Palace',
      description: 'The iconic winter palace of the Dalai Lama, a UNESCO World Heritage site and symbol of Tibetan Buddhism.',
      image: '🏰',
      features: ['UNESCO site', 'Historical artifacts', 'Religious significance', 'Panoramic views']
    },
    {
      id: 2,
      title: 'Jokhang Temple',
      description: 'The most sacred temple in Tibetan Buddhism, located in the heart of Lasa\'s old town.',
      image: '🛕',
      features: ['Pilgrimage site', 'Ancient architecture', 'Buddhist statues', 'Prayer wheels']
    },
    {
      id: 3,
      title: 'Norbulingka Palace',
      description: 'The summer palace of the Dalai Lama, featuring beautiful gardens and traditional Tibetan architecture.',
      image: '🌳',
      features: ['Summer palace', 'Gardens', 'Cultural performances', 'Historical exhibits']
    },
    {
      id: 4,
      title: 'Nam-tso Lake',
      description: 'One of the highest saltwater lakes in the world, offering breathtaking views of the Tibetan plateau.',
      image: '🏞️',
      features: ['High-altitude lake', 'Stunning scenery', 'Photography', 'Local nomad culture']
    }
  ]

  // Travel tips
  const travelTips = [
    {
      title: 'Altitude Adaptation',
      description: 'Spend 1-2 days acclimatizing to Lasa\'s high altitude (3,650m) before strenuous activities.',
      icon: '🏔️'
    },
    {
      title: 'Cultural Respect',
      description: 'Dress modestly, respect religious customs, and ask permission before photographing people.',
      icon: '🙏'
    },
    {
      title: 'Local Cuisine',
      description: 'Try Tibetan specialties like tsampa, butter tea, yak meat, and momos (dumplings).',
      icon: '🍵'
    },
    {
      title: 'Travel Permits',
      description: 'Foreign visitors need Tibet Travel Permits arranged through authorized tour operators.',
      icon: '📝'
    }
  ]

  return (
    <>
      <SEO 
        title="Discover the Spiritual Heart of Tibet in Lasa"
        description="Your comprehensive guide to Lasa tourism, attractions, culture, and travel tips. Explore Potala Palace, Jokhang Temple, Barkhor Street and more."
        keywords="Lasa, Tibet, travel, tourism, attractions,西藏,拉萨,旅游,景点,布达拉宫,大昭寺"
        type="website"
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Google-style Header */}
      <header className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                <span className="text-white text-lg font-bold">LH</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Hello Lasa</h1>
                <p className="text-gray-600 text-sm">Discover the Roof of the World</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(item.id)}
                  className={`px-4 py-2 rounded-lg transition-colors duration-200 ${
                    activeTab === item.id
                      ? 'bg-amber-50 text-amber-600 font-medium'
                      : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  {item.label}
                </button>
              ))}
            </nav>

            {/* Contact Button */}
            
          </div>
        </div>
      </header>

      {/* Domain Notification Banner */}
      {showDomainNotification && customDomain && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b border-blue-200">
          <div className="container mx-auto px-4 py-3">
            <div className="flex flex-col md:flex-row items-center justify-between gap-3">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm">🔗</span>
                </div>
                <div>
                  <p className="text-blue-900 font-medium">Custom domain available</p>
                  <p className="text-blue-700 text-sm">
                    This site is also accessible via custom domain
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <a 
                  href={customDomain}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-cyan-600 transition-all duration-200 shadow-sm"
                >
                  Visit via custom domain
                </a>
                <button 
                  onClick={() => setShowDomainNotification(false)}
                  className="text-blue-700 hover:text-blue-900 text-sm font-medium"
                >
                  Dismiss
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-amber-50 to-orange-100">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-amber-500">📍</span>
              <span className="text-sm font-medium">Tibet Autonomous Region</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
              Welcome to <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Lasa</span><br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Roof of the World</span>
            </h1>
            
            <p className="text-xl text-gray-700 mb-10 max-w-2xl">
              Discover the spiritual heart of Tibet, where ancient monasteries meet breathtaking Himalayan landscapes. 
              Experience rich Buddhist culture, stunning architecture, and warm Tibetan hospitality in this unique destination.
            </p>

            
          </div>
        </div>
      </section>

      <ShopSection />

      {/* Free Tickets Promotion */}
      <section className="py-12 bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full mb-6">
              <span className="text-white text-2xl">🎫</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Free <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Lasa Attraction Tickets</span>
            </h2>
            <p className="text-xl text-gray-700 mb-6 max-w-2xl mx-auto">
              Get up to 5 attraction tickets per person by sending an email
            </p>
            <div className="bg-white rounded-xl p-6 shadow-lg inline-block">
              <div className="flex flex-col md:flex-row items-center justify-center space-y-4 md:space-y-0 md:space-x-6">
                <div className="text-3xl">📧</div>
                <div className="text-left">
                  <p className="text-gray-800 font-medium">Email to Get Tickets</p>
                  <a 
                    href="mailto:info@hellolasa.com" 
                    className="text-lg font-semibold text-amber-600 hover:text-amber-800 transition-colors duration-200"
                  >
                    info@hellolasa.com
                  </a>
                </div>
                <a 
                  href="mailto:info@hellolasa.com"
                  className="px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-semibold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-200 shadow-md"
                >
                  Get Tickets Now
                </a>
              </div>
            </div>
            <p className="text-gray-600 text-sm mt-6">
              Limited tickets available, first come first served. Valid for Lasa attractions only.
            </p>
          </div>
        </div>
      </section>

      {/* News Section */}
      <NewsSection 
        website="hellolasa"
        limit={10000}
        title="Lasa Travel News"
        themeColor="purple"
      />

      {/* Attractions Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Sacred <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Sites</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Discover the most important spiritual and cultural destinations in Lasa
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {attractions.map((attraction) => (
              <div key={attraction.id} className="bg-white rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                <div className="p-6">
                  <div className="text-4xl mb-4">{attraction.image}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{attraction.title}</h3>
                  <p className="text-gray-600 mb-4">{attraction.description}</p>
                  
                  <div className="space-y-2 mb-6">
                    {attraction.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2 text-gray-700">
                        <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                        <span className="text-sm">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Travel Tips */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-amber-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Travel <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Tips</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Essential information for your Lasa journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {travelTips.map((tip, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="text-3xl mb-4">{tip.icon}</div>
                <h4 className="text-lg font-bold text-gray-900 mb-3">{tip.title}</h4>
                <p className="text-gray-600">{tip.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 to-orange-600">Touch</span>
            </h2>
            <p className="text-gray-600 text-lg mb-8">
              For travel inquiries and assistance with your Lasa journey
            </p>
            
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-8 border border-amber-100">
              <div className="text-4xl mb-4">📧</div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Contact Email</h3>
              <p className="text-gray-700 text-lg mb-6">
                Reach out to us for travel information and assistance
              </p>
              
              <div className="inline-flex items-center justify-center space-x-3 bg-white rounded-xl px-6 py-3.5 shadow-sm">
                <span className="text-amber-500 text-xl">✉️</span>
                <a 
                  href="mailto:info@hellolasa.com" 
                  className="text-xl font-semibold text-gray-900 hover:text-amber-600 transition-colors duration-200"
                >
                  info@hellolasa.com
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                  <span className="text-white text-lg font-bold">LH</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold">Hello Lasa</h3>
                  <p className="text-gray-400 text-sm">Lasa Travel Guide</p>
                </div>
              </div>
              <p className="text-gray-400">
                Your comprehensive guide to discovering the spiritual and cultural treasures of Lasa, Tibet.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Contact</h4>
              <div className="space-y-4 text-gray-400">
                <div className="flex items-center space-x-3">
                  <span>📧</span>
                  <a href="mailto:info@hellolasa.com" className="hover:text-white transition-colors duration-200">
                    info@hellolasa.com
                  </a>
                </div>
                <p className="text-sm text-gray-500">
                  For travel information and assistance with your Lasa journey
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-6">Explore More Destinations</h4>
              <ul className="space-y-3">
                {destinations.map((destination) => (
                  <li key={destination.name}>
                    <a 
                      href={destination.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors duration-200"
                    >
                      <span className="text-lg">{destination.emoji}</span>
                      <span>
                        <span className="font-medium">{destination.name}</span>
                        <span className="block text-sm text-gray-500">{destination.description}</span>
                      </span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Domain Information */}
            <div>
              <h4 className="text-lg font-bold mb-6">Domain Information</h4>
              <div className="space-y-4 text-gray-400">
                <div>
                  <p className="text-sm text-gray-500 mb-2">Current URL:</p>
                  <div className="flex items-center space-x-2">
                    <span className="text-lg">🌐</span>
                    <a 
                      href={currentUrl} 
                      className="text-white hover:text-amber-400 transition-colors duration-200 break-all"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {currentUrl || 'Loading...'}
                    </a>
                  </div>
                </div>
                
                {customDomain && (
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Custom Domain:</p>
                    <div className="flex items-center space-x-2">
                      <span className="text-lg">🔗</span>
                      <a 
                        href={customDomain} 
                        className="text-white hover:text-amber-400 transition-colors duration-200 break-all"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {customDomain}
                      </a>
                    </div>
                    {currentUrl.includes('.pages.dev') && (
                      <p className="text-xs text-amber-400 mt-2">
                        This site is also accessible via custom domain
                      </p>
                    )}
                  </div>
                )}
                
                <div className="pt-4 border-t border-gray-800">
                  <p className="text-sm text-gray-500 mb-2">Domain Status:</p>
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                    customDomain && currentUrl.includes('.pages.dev') 
                      ? 'bg-green-100 text-green-800 border border-green-300' 
                      : 'bg-blue-100 text-blue-800 border border-blue-300'
                  }`}>
                    {customDomain && currentUrl.includes('.pages.dev') 
                      ? 'Custom domain available' 
                      : 'Using primary domain'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© 2026 Hello Lasa. All rights reserved. | Lasa Travel Information Portal</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  )
}

export default App